import React from 'react';
import { tokens } from '../design-system';

/**
 * Compute word-level diff using longest common subsequence.
 * Returns array of { text, type } where type is 'common', 'removed', or 'added'.
 */
function computeWordDiff(oldText, newText) {
  const oldWords = (oldText || '').split(/(\s+)/);
  const newWords = (newText || '').split(/(\s+)/);

  const m = oldWords.length;
  const n = newWords.length;

  // Build LCS table
  let dp;
if (m * n > 10000000) {
  // Fallback for large inputs: treat whole texts as added/removed
  return [
    { text: oldText, type: 'removed' },
    { text: newText, type: 'added' },
  ];
}
dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to get diff
  const result = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.unshift({ text: oldWords[i - 1], type: 'common' });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ text: newWords[j - 1], type: 'added' });
      j--;
    } else {
      result.unshift({ text: oldWords[i - 1], type: 'removed' });
      i--;
    }
  }

  return result;
}

/**
 * Normalize a value to a diffable string.
 */
function toText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(v => typeof v === 'string' ? v : JSON.stringify(v, null, 2)).join('\n');
  if (typeof value === 'object') {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
  return String(value);
}

/**
 * TextDiff - Renders a word-level inline diff between two text values.
 *
 * Props:
 *   oldText - Original value (string, array, or object)
 *   newText - New value (string, array, or object)
 */
const TextDiff = ({ oldText, newText }) => {
  const oldStr = toText(oldText);
  const newStr = toText(newText);

  if (!oldStr && !newStr) return null;
  if (oldStr === newStr) {
    return <span style={styles.common}>{oldStr}</span>;
  }

  const diff = computeWordDiff(oldStr, newStr);

  return (
    <div style={styles.container}>
      {diff.map((part, i) => {
        if (part.type === 'removed') {
          return <span key={i} style={styles.removed}>{part.text}</span>;
        }
        if (part.type === 'added') {
          return <span key={i} style={styles.added}>{part.text}</span>;
        }
        return <span key={i} style={styles.common}>{part.text}</span>;
      })}
    </div>
  );
};

const styles = {
  container: {
    fontSize: tokens.typography.fontSize.sm[0],
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: tokens.typography.fontFamily.mono.join(', '),
  },
  removed: {
    backgroundColor: `${tokens.colors.error[500]}20`,
    color: tokens.colors.error[500],
    textDecoration: 'line-through',
    borderRadius: '2px',
    padding: '0 1px',
  },
  added: {
    backgroundColor: `${tokens.colors.success[500]}20`,
    color: tokens.colors.success[500],
    borderRadius: '2px',
    padding: '0 1px',
  },
  common: {},
};

export default TextDiff;
